/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Auth, Hub } from "aws-amplify";
import { CONNECTION_STATE_CHANGE } from '@aws-amplify/pubsub';

// react-router components
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Material Dashboard 2 PRO React themes
import theme from "assets/theme";

// Material Dashboard 2 PRO React routes
import routes from "router/routes";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController } from "context";

// Images
import brandLogo from "assets/images/ELCOS_logo_color_woSrl_v1.svg";
import miniBrandLogo from "assets/images/ELCOS_logo_color_woText_v1.svg";

// Paypal
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import paypalConfig from "common/configs/paypalConfig";

// Custom components
import Sidenav from "components/Sidenav";
import ProtectedRoute from "router/ProtectedRoute";
import { handleUserData, handleIsAuthenticating, handleIsLoggedIn } from "redux/slices/user";
import { useSelector } from "react-redux";
import { setMiniSidenav } from "context";
import MqttService from "common/services/mqtt/MqttService";
import { addTelemetrySubscriptions, addOtaSubscriptions, setTopicsToSubscribe, addEventsSubscriptions } from "redux/slices/mqtt";
import { deviceStatusUpdated } from "redux/slices/gateways";
import { deviceOtaReceived } from "redux/slices/ota";
import { ToastContextProvider } from "hooks/useToastNotification";
import { deviceTelemetryReceived } from "redux/slices/device";
import { getDeviceData } from "redux/slices/device";
import { Suspense } from "react";
import LoadingPage from "components/LoadingPage";
import { ErrorBoundary } from "react-error-boundary";
import UncaughtError from "features/errors/UncaughtError";
import AuthService from "common/services/users/AuthService";
import DevicesService from "common/services/devices/DevicesService";



export default function App() {
  const [ controller, dispatch ] = useMaterialUIController();
  const { direction, sidenavColor, miniSidenav, layout } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();
  const reduxDispatch = useDispatch();
  const userLevel = useSelector(state => state.user.level);
  const navigate = useNavigate();
  const telemetryTopics = useSelector(state => state.mqtt.telemetryTopics);
  const otaTopics = useSelector(state => state.mqtt.otaTopics);
  const eventsTopics = useSelector(state => state.mqtt.eventsTopics);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Checking user session
  useEffect(async () => {
    if (process.env.REACT_APP_ENVIRONMENT == "prod"){         // It should remove all console.logs from the application when in production
      console.log = () => {};
    }
    reduxDispatch(handleIsAuthenticating(true));
    try {
      const session = await AuthService.getCurrentSession();
      reduxDispatch(handleUserData(session));
      reduxDispatch(handleIsLoggedIn(true));

      // Listen to AWS IoT websocket connection state
      Hub.listen('pubsub', (data) => {
        const { payload } = data;
        if (payload.event === CONNECTION_STATE_CHANGE) {
          const connectionState = payload.data.connectionState;
          console.log(`MQTT WebSocket state: ${connectionState}`);
          // TODO: Handle disconnected state (show alert)
        }
      });

      // AWS IoT Core client inizialization
      const loginData = await MqttService.initProvider(session);
      reduxDispatch(setTopicsToSubscribe(loginData));

      reduxDispatch(handleIsAuthenticating(false));

    } catch(error) {
      reduxDispatch(handleIsAuthenticating(false));
      reduxDispatch(handleIsLoggedIn(false));
      
      if (pathname == "/support") {
        navigate("/support");
      } else {
        navigate("/login");
      }
    }
  }, []);

  useEffect(async () => {

    // IoT Events subscription callbacks
    const onEventMessage = async (msg) => { 
      console.log("Event received:");
      console.log(msg.value);
      reduxDispatch(deviceStatusUpdated({...msg.value, level: userLevel}));

      // CASE: Gateway Offline
      if (msg.value.id && msg.value?.body?.connectionStatus === "offline") {
        console.log("Puting devices offline...");
        const response = await DevicesService.getGateway(msg.value.id);
        const gateway = response?.data;
        console.log("[APP] - Gateway Offline: ", gateway);

        if (gateway?.devices && Array.isArray(gateway.devices)) {
          gateway.devices.forEach(device => {
            console.log("Device ID offline: ", device.id);
            reduxDispatch(getDeviceData({ gatewayId: msg.value.id, deviceId: device.id}));
          });
        }
      }
    };

    const onTelemetryMessage = (msg) => {
      console.log("Telemetry received");
      console.log(msg.value);

      reduxDispatch(deviceTelemetryReceived(msg.value));
    }

    const onOtaMessage = (msg) => {
      console.log("Ota received");
      console.log(msg.value);

      reduxDispatch(deviceOtaReceived(msg.value));
    }

    const onError = (err) => { console.log("MQTT subscribe onError called: "); console.log(err); };

    if (telemetryTopics.length) {
      const telemetrySub = MqttService.subscribe(telemetryTopics, onTelemetryMessage, onError);
      console.log("Subscribed to Telemetries");
      reduxDispatch(addTelemetrySubscriptions(telemetrySub));
    }
    if (otaTopics.length) {
      const otaSub = MqttService.subscribe(otaTopics, onOtaMessage, onError);
      console.log("Subscribed to Ota");
      reduxDispatch(addOtaSubscriptions(otaSub));
    }
    if (eventsTopics.length) {
      const eventSub = MqttService.subscribe(eventsTopics, onEventMessage, onError);
      console.log("Subscribed to Events");
      reduxDispatch(addEventsSubscriptions(eventSub));
    }
  }, [telemetryTopics, otaTopics])

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        if (route.authRequired) {
          return (
            <Route exact path={route.route} element={<ProtectedRoute element={route.component} level={route.level} />} key={route.key} />
          );
        }
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });

  return (
      <ThemeProvider theme={theme}>
        <ToastContextProvider>
          <PayPalScriptProvider options={paypalConfig}>
            <CssBaseline />
            <ErrorBoundary FallbackComponent={UncaughtError}>
              <Suspense fallback={<LoadingPage />}>
                {layout === 'dashboard' && (
                  <Sidenav
                    color={sidenavColor}
                    brand={brandLogo}
                    miniBrand={miniBrandLogo}
                    routes={routes}
                    onMouseEnter={handleOnMouseEnter}
                    onMouseLeave={handleOnMouseLeave}
                    userLevel={userLevel}
                  />
                )}
                <Routes>
                  {getRoutes(routes)}
                  <Route path="/" exact element={<Navigate to="/home" />} />
                  <Route path="*" element={<p>Not found: 404!</p>} />
                </Routes>  
              </Suspense>
            </ErrorBoundary>
          </PayPalScriptProvider>
        </ToastContextProvider>
      </ThemeProvider>
  );
}
